/**
 * Workspace: cached snapshot (offline-first), realtime connection with replay (hello.lastSeq), reconnect
 * with backoff, resync on gaps, ephemeral live state (presence, typing, agents), and the connectivity banner.
 */
import type { ReportReason } from "@bundle/contracts";
import { reportNote } from "../logic/moderation.ts";
import type { AvailableCommand, EphemeralMessage, CreateAgentRequest, CreateInvitesRequest, Invite, InviteGuestRequest, InviteGuestResponse, ClientFrame, HuddleSignal, NotificationEvent, PersistentEvent, ServerFrame, Workflow, WorkflowRun } from "@bundle/contracts";
import type { KitLabels } from "../logic/blockKit.ts";
import { connectivityBanner, type Banner, type RealtimeStatus } from "../logic/connectivity.ts";
import { nextMembersDndChange } from "../logic/dnd.ts";
import { nextStatusExpiry } from "../logic/profile.ts";
import { applyEphemeral, applyOnline, emptyLive, messageArrived, type LiveState } from "../logic/live.ts";
import { queuedCount } from "../logic/outbox.ts";
import { nextTypingExpiry, TYPING_TTL_MS } from "../logic/typing.ts";
import { dmChannelWith, type WorkspaceCache } from "../logic/workspace.ts";
import { botName, localizeMembers } from "../logic/support.ts";
import type { MessageDescriptor } from "../i18n.ts";
import {
  errorKind,
  type ApiClient,
  type Clock,
  type CreateAgentResult,
  type DataSource,
  type EditorDocs,
  type EmojiCatalog,
  type IdGen,
  type KeyValueStorage,
  type LiveQuery,
  type NetworkMonitor,
  type Platform,
  type RealtimeClient,
  type RealtimeConnection,
  type SnapshotStore,
} from "../ports.ts";
import { AppModalViewModel } from "./AppModalViewModel.ts";
import { MailThreadCache } from "./MailThreadCache.ts";
import { AppHomeViewModel } from "./AppHomeViewModel.ts";
import { DraftsViewModel } from "./DraftsViewModel.ts";
import { ViewModel } from "./ViewModel.ts";

export type WorkspaceDeps = {
  api: ApiClient;
  data: DataSource;
  realtime: RealtimeClient;
  clock: Clock;
  ids: IdGen;
  storage: KeyValueStorage;
  /** Device snapshots for an instant open (mail's list, Home's digest, the threads I've read). Absent = they start from the server. */
  snapshots?: SnapshotStore;
  network: NetworkMonitor;
  platform: Platform;
  clientVersion: string;
  /** Page bodies (Loro). Absent in tests and builds without docs. */
  docs?: EditorDocs;
  /** Emoji shortcodes for the composer's ":" autocomplete. Absent = no emoji suggestions. */
  emoji?: EmojiCatalog;
  /** Composer autocomplete (@ # : / menus, the @ button opening the menu). Absent/false = off (feature flag). */
  autocomplete?: boolean;
  /** A message that should notify me arrived (realtime `notification`); the platform decides whether to show it. */
  onNotification?: (event: NotificationEvent) => void;
  /** A live huddle signal (captions, drawing) for the huddle view model. */
  onHuddleSignal?: (signal: HuddleSignal) => void;
  /** A workspace event was applied (Activity refreshes on the ones that may concern me: ActivityViewModel.onEvent). */
  onEvent?: (event: PersistentEvent) => void;
  /**
   * Resolves a descriptor in the viewer's language: names the client translates (the Support bot) are applied to
   * `state.cache.members` with it. Absent = stored names. Call `relocalize()` after the language changes.
   */
  translate?: (d: MessageDescriptor) => string;
};

export type WorkspaceState = {
  wsId: string;
  meId: string;
  /** "loading" only until the first cached or fetched snapshot. */
  status: "loading" | "ready" | "error";
  cache?: WorkspaceCache;
  live: LiveState;
  realtime: RealtimeStatus;
  network: boolean;
  /** Messages waiting in the outbox. */
  queued: number;
  banner?: Banner;
  /** The server rejected our token — the session must sign in again. */
  authRejected: boolean;
  error?: string;
  /**
   * When someone's Do not disturb last switched on or off by the clock (a pause ran out, quiet hours started or
   * ended). Views that show moons recompute when it moves (logic/dnd nextMembersDndChange).
   */
  dndAt?: number;
  /**
   * When someone's custom status last ran out. Views that show status emoji recompute when it moves, so a status
   * disappears at its expiry moment (logic/profile nextStatusExpiry, activeStatus).
   */
  statusAt?: number;
  /**
   * Messages only I see ("Only visible to you", contracts apps.ts EphemeralMessage) from slash commands and buttons,
   * in arrival order; each shows after the newest message created before it. In memory only: gone on reload, like
   * Slack.
   */
  ephemerals?: EphemeralMessage[];
  /** Slash commands of apps installed here (listCommands), once loaded; absent = not loaded / not supported. */
  appCommands?: AvailableCommand[];
  /** Workflows I can run or manage (listWorkflows), once loaded; absent = not loaded / not supported. */
  workflows?: Workflow[];
};


/** Longest single wait for a Do not disturb boundary before re-checking. */
const DND_RECHECK_MS = 6 * 60 * 60 * 1000;

/** Reconnect delays (ms); the last one repeats. */
export const RECONNECT_BACKOFF = [500, 1000, 2000, 5000, 10000, 30000];

/**
 * Gateway errors about one client frame (a bad typing frame, a channel I'm not in): the connection itself is fine.
 * Any other non-auth error means the connection failed (e.g. its hello/replay): the gateway won't send it anything.
 */
const FRAME_ERRORS: ReadonlySet<string> = new Set(["bad_frame", "not_in_channel"]);

export class WorkspaceViewModel extends ViewModel<WorkspaceState> {
  readonly deps: WorkspaceDeps;
  private readonly query: LiveQuery<WorkspaceCache>;
  private conn: RealtimeConnection | undefined;
  private attempt = 0;
  /** Connections in a row whose hello failed after `welcome`: keeps the backoff growing until one delivers an event. */
  private failedHellos = 0;
  private cancelReconnect: (() => void) | undefined;
  private cancelTypingTimer: (() => void) | undefined;
  private cancelDndTimer: (() => void) | undefined;
  private dndTimerAt: number | undefined;
  private cancelStatusTimer: (() => void) | undefined;
  private statusTimerAt: number | undefined;
  private resyncing = false;
  private started = false;
  private mailThreadCache: MailThreadCache | undefined;

  constructor(deps: WorkspaceDeps, wsId: string, meId: string) {
    super({
      wsId,
      meId,
      status: "loading",
      live: emptyLive(),
      realtime: "connecting",
      network: deps.network.getSnapshot(),
      queued: 0,
      authRejected: false,
    });
    this.deps = deps;
    this.query = deps.data.workspace(wsId);
    const outbox = deps.data.outbox(wsId);
    this.onDispose(this.query.subscribe(() => this.syncQuery()));
    this.onDispose(outbox.subscribe(() => this.updateDerived({ queued: queuedCount(outbox.getSnapshot()) })));
    this.onDispose(deps.network.subscribe(() => this.onNetwork()));
    this.onDispose(() => this.disconnect());
    this.onDispose(() => this.cancelTypingTimer?.());
    this.onDispose(() => this.cancelDndTimer?.());
    this.onDispose(() => this.cancelStatusTimer?.());
    this.updateDerived({ queued: queuedCount(outbox.getSnapshot()) });
    this.syncQuery();
  }

  get wsId(): string {
    return this.state.wsId;
  }

  /**
   * Options external selects got from their apps as people typed (KitPickerViewModel remembers them), so a picked
   * option reaches the app with its text in `state.values` / block_actions (logic/blockKit KitLabels).
   */
  readonly kitLabels: KitLabels = {};

  rememberKitOptions(blockId: string, actionId: string, options: readonly { value: string; label: string }[]): void {
    const byAction = (this.kitLabels[blockId] ??= {});
    const labels = (byAction[actionId] ??= {});
    for (const o of options) labels[o.value] = o.label;
  }

  private draftsVm: DraftsViewModel | undefined;

  private modalVm: AppModalViewModel | undefined;
  private readonly homeVms = new Map<string, AppHomeViewModel>();

  private readonly keptVms = new Map<string, { dispose: () => void }>();

  /**
   * A screen's view model kept for this workspace's lifetime rather than the screen's (Home's digests), so switching
   * workspaces and back shows it at once while it refreshes.
   */
  keep<T extends { dispose: () => void }>(key: string, create: () => T): T {
    let vm = this.keptVms.get(key) as T | undefined;
    if (vm === undefined) {
      const made = create();
      this.keptVms.set(key, made);
      this.onDispose(() => made.dispose());
      vm = made;
    }
    return vm;
  }

  /** An app's Home tab for me (one per app; the app's DM shows it). */
  appHome(appId: string): AppHomeViewModel {
    let vm = this.homeVms.get(appId);
    if (vm === undefined) {
      const made = new AppHomeViewModel(this, appId);
      this.homeVms.set(appId, made);
      this.onDispose(() => made.dispose());
      vm = made;
    }
    return vm;
  }

  /** The modal an app opened for me (contracts apps.ts views; one per workspace). */
  get modal(): AppModalViewModel {
    if (this.modalVm === undefined) {
      const vm = new AppModalViewModel(this);
      this.modalVm = vm;
      this.onDispose(() => vm.dispose());
    }
    return this.modalVm;
  }

  /** My drafts and their sync with the server (one per workspace; composers and the Drafts & sent list share it). */
  /** The threads kept on this device for the reader (absent without snapshots). */
  get mailThreads(): MailThreadCache | undefined {
    if (this.deps.snapshots === undefined) return undefined;
    this.mailThreadCache ??= new MailThreadCache(this.deps.snapshots, this.wsId, this.state.meId);
    return this.mailThreadCache;
  }

  get drafts(): DraftsViewModel {
    if (this.draftsVm === undefined) {
      const vm = new DraftsViewModel(this);
      this.draftsVm = vm;
      this.onDispose(() => vm.dispose());
    }
    return this.draftsVm;
  }

  private lastRaw: WorkspaceCache | undefined;
  private lastLocalized: WorkspaceCache | undefined;

  /** The cache with client-translated names (Support), memoized on the snapshot. */
  private localized(raw: WorkspaceCache): WorkspaceCache {
    const translate = this.deps.translate;
    if (translate === undefined) return raw;
    if (raw === this.lastRaw && this.lastLocalized !== undefined) return this.lastLocalized;
    const members = localizeMembers(raw.members, translate(botName()));
    const out = members === raw.members ? raw : { ...raw, members };
    this.lastRaw = raw;
    this.lastLocalized = out;
    return out;
  }

  /** The viewer's language changed: re-applies translated names. */
  relocalize(): void {
    this.lastRaw = undefined;
    this.lastLocalized = undefined;
    this.syncQuery();
  }

  /** Starts realtime once a snapshot exists (so hello.lastSeq matches the cache). */
  start(): void {
    this.started = true;
    this.maybeConnect();
  }

  /**
   * Another workspace came to the front (the switcher keeps this one warm, like Slack): the socket closes and no
   * reconnect is scheduled, while the cache and everything built on it stay for an instant return.
   */
  suspend(): void {
    if (!this.started) return;
    this.started = false;
    this.disconnect();
    // Not "offline": that shows "Reconnecting…", and on return the connection is simply opening.
    this.updateDerived({ realtime: "connecting" });
  }

  /** Back in front: reconnects, and the hello's lastSeq replays what happened meanwhile. */
  resume(): void {
    if (this.started || this.isDisposed) return;
    this.attempt = 0;
    this.failedHellos = 0;
    this.start();
  }

  /** Whether the realtime connection is wanted (started and not suspended). */
  get active(): boolean {
    return this.started;
  }

  refresh(): Promise<void> {
    return this.query.refresh();
  }

  private syncQuery(): void {
    const q = this.query.getSnapshot();
    const patch: Partial<WorkspaceState> = {};
    if (q.data !== undefined) {
      patch.cache = this.localized(q.data);
      patch.status = "ready";
      if (this.state.meId !== q.data.meId) patch.meId = q.data.meId;
    } else if (q.status === "error") {
      patch.status = "error";
    }
    if (q.errorKind === "auth") patch.authRejected = true;
    if (q.status === "error" && q.error !== undefined) patch.error = q.error;
    this.updateDerived(patch);
    this.scheduleDnd();
    this.scheduleStatusExpiry();
    this.maybeConnect();
  }

  /** Re-renders moons when the next Do not disturb boundary among the members passes (one timer for everyone). */
  private scheduleDnd(): void {
    const members = this.state.cache?.members;
    const now = this.deps.clock.now();
    const next = members === undefined ? undefined : nextMembersDndChange(members, now);
    if (next === this.dndTimerAt) return;
    this.cancelDndTimer?.();
    this.cancelDndTimer = undefined;
    this.dndTimerAt = next;
    if (next === undefined) return;
    // Long waits are re-checked every few hours (timers drift and far-off pauses may change meanwhile).
    this.cancelDndTimer = this.deps.clock.setTimer(Math.min(next - now, DND_RECHECK_MS), () => {
      this.cancelDndTimer = undefined;
      this.dndTimerAt = undefined;
      const t = this.deps.clock.now();
      if (t >= next) this.updateDerived({ dndAt: t });
      this.scheduleDnd();
    });
  }

  /** Hides custom statuses the moment the next one among the members runs out (one timer for everyone). */
  private scheduleStatusExpiry(): void {
    const members = this.state.cache?.members;
    const now = this.deps.clock.now();
    const next = members === undefined ? undefined : nextStatusExpiry(members, now);
    if (next === this.statusTimerAt) return;
    this.cancelStatusTimer?.();
    this.cancelStatusTimer = undefined;
    this.statusTimerAt = next;
    if (next === undefined) return;
    this.cancelStatusTimer = this.deps.clock.setTimer(Math.min(next - now, DND_RECHECK_MS), () => {
      this.cancelStatusTimer = undefined;
      this.statusTimerAt = undefined;
      const t = this.deps.clock.now();
      if (t >= next) this.updateDerived({ statusAt: t });
      this.scheduleStatusExpiry();
    });
  }

  /** Applies a patch and recomputes the connectivity banner. */
  private updateDerived(patch: Partial<WorkspaceState>): void {
    const prev = this.state;
    const next: WorkspaceState = { ...prev, ...patch };
    const banner = connectivityBanner({
      network: next.network,
      realtime: next.realtime,
      queued: next.queued,
      hasCache: next.cache !== undefined,
    });
    const sameBanner =
      banner === undefined
        ? prev.banner === undefined
        : prev.banner !== undefined && prev.banner.tone === banner.tone && JSON.stringify(prev.banner.label) === JSON.stringify(banner.label);
    if (sameBanner) {
      if (prev.banner === undefined) delete next.banner;
      else next.banner = prev.banner;
    } else if (banner === undefined) delete next.banner;
    else next.banner = banner;
    let changed = !sameBanner;
    for (const k of Object.keys(patch) as (keyof WorkspaceState)[]) if (!Object.is(prev[k], next[k])) changed = true;
    if (changed) this.setState(next);
  }

  private onNetwork(): void {
    const online = this.deps.network.getSnapshot();
    this.updateDerived({ network: online });
    if (online) {
      this.attempt = 0;
      this.cancelReconnect?.();
      this.cancelReconnect = undefined;
      this.maybeConnect();
    } else {
      this.disconnect();
      this.updateDerived({ realtime: "offline" });
    }
  }

  private maybeConnect(): void {
    if (!this.started || this.isDisposed || this.conn !== undefined || this.cancelReconnect !== undefined) return;
    if (this.state.cache === undefined || this.state.authRejected) return;
    if (!this.state.network) {
      this.updateDerived({ realtime: "offline" });
      return;
    }
    void this.connect();
  }

  private async connect(): Promise<void> {
    const ws = this.state.wsId;
    this.updateDerived({ realtime: this.state.realtime === "online" ? "online" : "connecting" });
    // Placeholder so concurrent maybeConnect calls don't open twice while the ticket is fetched.
    let cancelled = false;
    const placeholder: RealtimeConnection = { send: () => {}, close: () => { cancelled = true; } };
    this.conn = placeholder;
    let url: string;
    try {
      url = (await this.deps.api.realtimeTicket(ws)).url;
    } catch (e) {
      if (this.conn === placeholder) this.conn = undefined;
      if (errorKind(e) === "auth") {
        this.updateDerived({ authRejected: true, realtime: "offline" });
        return;
      }
      this.scheduleReconnect();
      return;
    }
    if (cancelled || this.isDisposed || this.conn !== placeholder) return;
    this.conn = this.deps.realtime.connect(url, {
      onOpen: () => this.onOpen(),
      onFrame: (f) => this.onFrame(f),
      onClose: () => this.onClose(),
    });
  }

  private onOpen(): void {
    const frame: { op: "hello"; platform: Platform; clientVersion: string; lastSeq?: number } = {
      op: "hello",
      platform: this.deps.platform,
      clientVersion: this.deps.clientVersion,
    };
    const seq = this.deps.data.cachedSeq(this.state.wsId);
    if (seq !== undefined) frame.lastSeq = seq;
    this.conn?.send(frame);
  }

  private onFrame(frame: ServerFrame): void {
    switch (frame.op) {
      case "welcome": {
        this.attempt = this.failedHellos;
        const cache = this.state.cache;
        const ids = cache === undefined ? [] : Object.keys(cache.members);
        this.updateDerived({ realtime: "online", live: applyOnline(this.state.live, ids, frame.onlineUserIds) });
        // A modal opened or closed while I was away (only once one was shown here: no request per reconnect otherwise).
        if (this.modalVm?.state.stack !== undefined) this.modalVm.load();
        return;
      }
      case "event": {
        this.failedHellos = 0; // the replay got through: this connection is live
        const result = this.deps.data.applyEnvelope(this.state.wsId, frame.envelope, frame.prevSeq);
        const ev = frame.envelope.event;
        if (ev.type === "message.created") {
          this.updateDerived({ live: messageArrived(this.state.live, ev.message.authorId, ev.message.channelId) });
        }
        this.deps.onEvent?.(ev);
        if (result === "gap") void this.resync();
        return;
      }
      case "ephemeral":
        if (frame.event.type === "app.ephemeral") {
          this.addEphemeral(frame.event.message);
          return;
        }
        if (frame.event.type === "app.views") {
          this.modal.apply(frame.event.views);
          return;
        }
        if (frame.event.type === "app.home") {
          this.homeVms.get(frame.event.home.appId)?.apply(frame.event.home);
          return;
        }
        if (frame.event.type === "notification") {
          this.deps.onNotification?.(frame.event);
          return;
        }
        this.updateDerived({ live: applyEphemeral(this.state.live, frame.event, this.state.meId, this.deps.clock.now()) });
        if (frame.event.type === "typing") this.scheduleTypingPrune();
        return;
      case "huddle":
        this.deps.onHuddleSignal?.(frame.signal);
        return;
      case "resync":
        void this.resync();
        return;
      case "error":
        if (frame.code === "unauthorized" || frame.code === "auth") {
          this.updateDerived({ authRejected: true });
          return;
        }
        if (FRAME_ERRORS.has(frame.code)) return;
        // The connection is unusable (e.g. the gateway couldn't replay for my hello). Don't sit on it — it would never
        // deliver events or notifications — reconnect with backoff (the gateway closes it too).
        this.failedHellos += 1;
        this.attempt = Math.max(this.attempt, this.failedHellos);
        this.disconnect();
        this.scheduleReconnect();
        return;
    }
  }

  private onClose(): void {
    this.conn = undefined;
    // Suspended (another workspace is in front): nothing to report, and resume() reconnects.
    if (this.isDisposed || !this.started) return;
    this.updateDerived({ realtime: "offline" });
    if (this.state.network && !this.state.authRejected) this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    if (this.isDisposed || this.cancelReconnect !== undefined) return;
    const delay = RECONNECT_BACKOFF[Math.min(this.attempt, RECONNECT_BACKOFF.length - 1)]!;
    this.attempt += 1;
    this.updateDerived({ realtime: "offline" });
    this.cancelReconnect = this.deps.clock.setTimer(delay, () => {
      this.cancelReconnect = undefined;
      this.maybeConnect();
    });
  }

  private disconnect(): void {
    this.cancelReconnect?.();
    this.cancelReconnect = undefined;
    const c = this.conn;
    this.conn = undefined;
    c?.close();
  }

  /** Refetch the snapshot, then reconnect so replay starts from the fresh seq. */
  async resync(): Promise<void> {
    if (this.resyncing) return;
    this.resyncing = true;
    this.disconnect();
    this.updateDerived({ realtime: "connecting" });
    try {
      await this.deps.data.resync(this.state.wsId);
    } catch {
      // Keep showing the cache; the reconnect below retries.
    } finally {
      this.resyncing = false;
    }
    this.maybeConnect();
  }

  /** After the session renewed its token: clear the rejection, refetch and reconnect. */
  reauthenticated(): void {
    this.updateDerived({ authRejected: false });
    void this.query.refresh();
    this.maybeConnect();
  }

  /** The 1:1 DM channel with `userId`, created on the server if needed (online only). */
  async ensureDm(userId: string): Promise<string | undefined> {
    const cache = this.state.cache;
    const existing = cache === undefined ? undefined : dmChannelWith(cache, userId);
    if (existing !== undefined) return existing.id;
    try {
      const c = await this.deps.data.createChannel(this.state.wsId, { kind: "dm", name: "", memberIds: userId === this.state.meId ? [] : [userId] });
      return c.id;
    } catch {
      return undefined;
    }
  }

  /**
   * Block or unblock someone (Report & block; logic/moderation): optimistic in the cache (their messages collapse,
   * a 1:1 DM's composer is replaced at once), rolled back and rejected when the server refuses or I'm offline.
   */
  setBlocked(userId: string, blocked: boolean): Promise<void> {
    return this.deps.data.setBlocked(this.state.wsId, userId, blocked);
  }

  /** Reports a message to the workspace admins (reason + optional note, trimmed; online only). */
  reportMessage(messageId: string, reason: ReportReason, note?: string): Promise<void> {
    const n = reportNote(note);
    return this.deps.api.reportMessage(this.state.wsId, messageId, n === undefined ? { reason } : { reason, note: n });
  }

  inviteGuest(req: InviteGuestRequest): Promise<InviteGuestResponse> {
    return this.deps.data.inviteGuest(this.state.wsId, req);
  }

  /** Invite links / email invites for this workspace (online only). */
  createInvites(req: CreateInvitesRequest): Promise<Invite[]> {
    return this.deps.api.createInvites(this.state.wsId, req);
  }

  createAgent(req: CreateAgentRequest): Promise<CreateAgentResult> {
    return this.deps.data.createAgent(this.state.wsId, req);
  }

  /** Keeps a message only I see (from a command, a button or the `app.ephemeral` event); repeats are ignored. */
  addEphemeral(message: EphemeralMessage): void {
    const list = this.state.ephemerals ?? [];
    if (list.some((e) => e.id === message.id)) return;
    this.update({ ephemerals: [...list, message] });
  }

  dismissEphemeral(id: string): void {
    const list = this.state.ephemerals ?? [];
    if (list.some((e) => e.id === id)) this.update({ ephemerals: list.filter((e) => e.id !== id) });
  }

  /** Loads the app slash commands once (failures retry on the next call). */
  loadAppCommands(): void {
    const list = this.deps.api.listCommands;
    if (list === undefined || this.state.appCommands !== undefined || this.loadingCommands) return;
    this.loadingCommands = true;
    list.call(this.deps.api, this.state.wsId).then(
      (commands) => {
        this.loadingCommands = false;
        if (!this.isDisposed) this.update({ appCommands: commands });
      },
      () => {
        this.loadingCommands = false;
      },
    );
  }

  private loadingCommands = false;

  /** Loads the workflows I can run or manage (`force`: again, e.g. when a channel's workflows menu opens). */
  loadWorkflows(force = false): void {
    const list = this.deps.api.listWorkflows;
    if (list === undefined || this.loadingWorkflows || (!force && this.state.workflows !== undefined)) return;
    this.loadingWorkflows = true;
    list.call(this.deps.api, this.state.wsId).then(
      (res) => {
        this.loadingWorkflows = false;
        if (!this.isDisposed) this.update({ workflows: res.workflows });
      },
      () => {
        this.loadingWorkflows = false;
      },
    );
  }

  private loadingWorkflows = false;

  /**
   * Runs a link workflow from its channel. Its form (if any) opens on this client: the request carries a clientToken
   * the modal knows (AppModalViewModel). Rejects when the server refuses (not in the channel, unpublished).
   */
  async runWorkflow(workflowId: string, channelId: string): Promise<WorkflowRun> {
    const run = this.deps.api.runWorkflow;
    if (run === undefined) throw new Error("Workflows aren't supported here");
    return run.call(this.deps.api, this.state.wsId, workflowId, { channelId, clientToken: this.modal.newClientToken() });
  }

  /** Sends a frame over the realtime socket if connected (dropped otherwise: live-only frames). */
  sendFrame(frame: ClientFrame): void {
    if (this.state.realtime !== "online" || this.conn === undefined) return;
    this.conn.send(frame);
  }

  /** Sends a (caller-throttled) typing frame if connected. */
  sendTyping(channelId: string, threadRootId: string | undefined): void {
    if (this.state.realtime !== "online" || this.conn === undefined) return;
    this.conn.send(threadRootId === undefined ? { op: "typing", channelId } : { op: "typing", channelId, threadRootId });
  }

  private scheduleTypingPrune(): void {
    this.cancelTypingTimer?.();
    const now = this.deps.clock.now();
    const next = nextTypingExpiry(this.state.live.typing, now);
    if (next === undefined) return;
    this.cancelTypingTimer = this.deps.clock.setTimer(next - now + 1, () => {
      const t = this.deps.clock.now();
      const typing = this.state.live.typing.filter((e) => t - e.at < TYPING_TTL_MS);
      if (typing.length !== this.state.live.typing.length) this.updateDerived({ live: { ...this.state.live, typing } });
      this.scheduleTypingPrune();
    });
  }
}
